<template>
  <div v-if="activeCompany && initialized">
    <div class="card-header" v-if="showStringFilter">
      <CustomerStringFilter :isSearching="isSearching" v-on:updateSearch="filterByString" />
    </div>
    <div class="card-header">
      <div class="row">
        <div class="col-12 mb-0">
          <div class="mt-3 mb-2" v-if="showCustomerCount">
            <b-skeleton
              v-if="selectIsCountPending"
              animation="fade"
              width="90px"
              height="22px"
              class="mt-3 mb-2"
            />
            <template v-else-if="selectIsCountAvailable">
              <animated-number
                :value="count"
                :formatValue="parseInt"
                :duration="500"
                class="font-weight-bold customer-count"
              />
              <span class="customer-text"> total customers</span>
            </template>
          </div>
          <div class="mb-3 clearfix">
            <div class="dropdown d-inline-block mr-4 mb-2 pt--2 filter-container date-filter">
              <h6 class="header-pretitle mt-3">Date Added</h6>
              <CustomerDateFilter v-on:updateDateSettings="filterByDate" />
            </div>
            <div
              v-if="userActiveCompanyLocations.length > 1"
              class="dropdown d-inline-block mr-4 mb-2 filter-container"
            >
              <h6 class="header-pretitle mt-3">Location</h6>
              <CustomerGroupingFilter :forCampaign="campaignSource" @update="groupingUpdate" />
            </div>
            <div class="d-inline-block mb-2 mr-4 filter-container">
              <h6 class="header-pretitle mt-3">Behavior</h6>
              <CustomerActionFilter
                v-on:updateActions="filterByAction"
                :campaignSource="campaignSource"
              />
            </div>
            <div class="d-inline-block mb-2 mr-4 filter-container">
              <h6 class="header-pretitle mt-3">Rating</h6>
              <CustomerRatingFilter v-on:updateRatings="filterByRating" />
            </div>
            <div class="d-inline-block mb-2 mr-4 filter-container" v-if="!requireMarketingOptIn">
              <h6 class="header-pretitle mt-3">marketing opt-in</h6>
              <CustomerOptInFilter v-on:updateOptIns="filterByOptIn" />
            </div>
            <div class="d-inline-block mb-2 mr-4 filter-container">
              <h6 class="header-pretitle mt-3">Tags</h6>
              <MultiSelect
                taggable
                multiple
                hideCaret
                reposition
                hideSelected
                label="name"
                track-by="_id"
                v-model="tagsFilters"
                :searchable="false"
                class="tag-filters"
                id="customer-filters"
                :options="tagOptions"
                :show-labels="false"
                openDirection="below"
                tag-placeholder="Add"
                :showNoResults="false"
                :internal-search="true"
                ><template #placeholder>
                  <span class="text-secondary">
                    <span class="fas fa-hashtag"></span> All tags
                  </span>
                </template></MultiSelect
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters, mapActions } from 'vuex'
const UserModule = createNamespacedHelpers('user')
const CustomerModule = createNamespacedHelpers('customer')
const CompanyModule = createNamespacedHelpers('company')
const TagModule = createNamespacedHelpers('tag')
const GroupModule = createNamespacedHelpers('group')
const DataCoreModule = createNamespacedHelpers('dataCore')

import AnimatedNumber from 'animated-number-vue'
import CustomerRatingFilter from '@/components/Modules/Customer/CustomerFilters/CustomerRatingFilter'
import CustomerDateFilter from '@/components/Modules/Customer/CustomerFilters/CustomerDateFilter'
import CustomerStringFilter from '@/components/Modules/Customer/CustomerFilters/CustomerStringFilter'
import CustomerActionFilter from '@/components/Modules/Customer/CustomerFilters/CustomerActionFilter/CustomerActionFilter'
import CustomerOptInFilter from '@/components/Modules/Customer/CustomerFilters/CustomerOptInFilter'
import CustomerGroupingFilter from '@/components/Modules/Customer/CustomerFilters/CustomerGroupingFilter'
import MultiSelect from '@/components/Common/MultiSelect'
export const EVENT_LOADED_CUSTOMERS = 'loaded-customers'

// Status only. The list request body carries the search term, which is a guest name or phone
// number, matching the convention the store already holds at `customer.js`'s dispatch handler.
const logQueryFailure = (error) =>
  console.error('Failed to run the customer list query', error?.status)

export default {
  name: 'CustomersTableFilters',
  data: () => ({
    queryPending: false,
    filtersChangedWhilePending: false,
    tagOptions: [],
    tagsFilters: [],
    filteredGroups: null,
    filteredGroupMembers: null,
    filteredLocations: null,
    filteredDisabledLocations: null,
    initialized: false
  }),
  props: {
    campaignSource: {
      type: Boolean,
      default: false
    },
    showCustomerCount: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 50
    },
    showStringFilter: {
      type: Boolean,
      default: true
    },
    requireMarketingOptIn: {
      type: Boolean,
      default: false
    }
  },
  components: {
    CustomerRatingFilter,
    CustomerDateFilter,
    CustomerStringFilter,
    CustomerActionFilter,
    CustomerOptInFilter,
    MultiSelect,
    AnimatedNumber,
    CustomerGroupingFilter
  },
  async mounted() {
    await this.fetchGroups({
      memberType: 'location',
      companyIds: this.selectActiveCompanyIds
    })
    this.initialized = true
    this.$emit('filtersInitialized')
  },
  computed: {
    ...UserModule.mapState(['onlineUser']),
    ...CustomerModule.mapState(['listFilters', 'isSearching', 'count']),
    ...CustomerModule.mapGetters(['selectIsCountPending', 'selectIsCountAvailable']),
    ...mapGetters('location', ['userActiveCompanyLocations']),
    ...CompanyModule.mapState(['activeCompany']),
    ...TagModule.mapGetters(['companyTags']),
    ...DataCoreModule.mapGetters(['selectActiveCompanyIds'])
  },
  methods: {
    ...GroupModule.mapActions(['selectGroupLocationIds', 'fetchGroups']),
    ...mapActions('location', ['getUserLocationsByActiveCompany']),
    ...TagModule.mapActions(['getTags']),
    ...CustomerModule.mapMutations(['setListFilters', 'setSkipCount', 'setLimit']),
    ...CustomerModule.mapActions(['queryCustomers']),

    async groupingUpdate(val) {
      this.filteredGroups = val && val.groupIds && val.groupIds.length ? val.groupIds : null
      this.filteredGroupMembers =
        val && val.memberIds && val.memberIds.length ? val.memberIds : null
      this.filteredLocations =
        val && val.nonGroupMemberIds && val.nonGroupMemberIds.length ? val.nonGroupMemberIds : null
      this.filteredDisabledLocations =
        val && val.disabledNonGroupMemberIds && val.disabledNonGroupMemberIds.length
          ? val.disabledNonGroupMemberIds
          : null

      let locationsToFilter = [
        ...new Set([
          ...(this.filteredLocations || []),
          ...(this.filteredDisabledLocations || []),
          ...(this.filteredGroupMembers || [])
        ])
      ]

      this.applyFilters({
        ...this.listFilters,
        locations: locationsToFilter.length
          ? locationsToFilter
          : this.filteredGroups?.length
          ? await this.selectGroupLocationIds(this.filteredGroups)
          : []
      })
    },
    filterByDate(dateSettings = {}) {
      this.applyFilters({ ...this.listFilters, dateSettings })
    },
    filterByRating(ratings) {
      this.applyFilters({
        ...this.listFilters,
        surveyRatings: ratings.map((rating) => rating.score)
      })
    },
    filterByOptIn(optInValue) {
      if ('Opted In' === optInValue.title) {
        this.applyFilters({
          ...this.listFilters,
          marketingOptIn: [this.activeCompany._id],
          notMarketingOptIn: null
        })
      } else if ('Not Opted In' === optInValue.title) {
        this.applyFilters({
          ...this.listFilters,
          notMarketingOptIn: [this.activeCompany._id],
          marketingOptIn: null
        })
      } else {
        this.applyFilters({
          ...this.listFilters,
          notMarketingOptIn: null,
          marketingOptIn: null
        })
      }
    },
    filterByString(search) {
      this.applyFilters({
        ...this.listFilters,
        search
      })
    },
    filterByAction(actions) {
      this.applyFilters({
        ...this.listFilters,
        actions
      })
    },
    applyFilters(filters) {
      if (!filters.marketingOptIn && this.requireMarketingOptIn) {
        filters.marketingOptIn = [this.activeCompany._id]
      }

      this.setSkipCount(0)
      this.setListFilters(filters)

      // A change that lands mid-flight is recorded rather than dropped, and re-queried once the
      // request settles. Whether the in-flight dispatch also happens to carry it is path-dependent:
      // the async path reads `listFilters` after its channel wait, the nexus path at dispatch.
      // Either way the re-query is what makes the newer filters correct. See the note at that read
      // in `store/customer.js`.
      if (this.queryPending) {
        this.filtersChangedWhilePending = true
        return
      }

      this.queryPending = true
      // Only query once per render cycle. Caught here because nothing holds the promise a
      // `$nextTick` callback returns, so anything escaping `runQuery` would surface as an unhandled
      // rejection rather than a log.
      this.$nextTick(() => this.runQuery().catch(logQueryFailure))

      // Dispatched alongside the list rather than awaited with it: the tag list fills a filter
      // dropdown and never gates the table, so a request for it that never settles must not hold
      // `loaded-customers` back over rows the page has already received.
      this.getTags().catch((error) => console.error('Failed to load tags', error?.status))
    },

    async runQuery() {
      // Cleared here rather than in applyFilters: every change committed before this point is
      // already in `listFilters` and so is carried by the dispatch below. Only what lands from here
      // on needs a query of its own.
      this.filtersChangedWhilePending = false

      try {
        await this.queryCustomers({})
      } finally {
        this.$emit(EVENT_LOADED_CUSTOMERS)
        this.queryPending = false

        // Inside `finally` so a rejected request still re-queries; the queued change is otherwise
        // lost exactly when the user is most likely to try again.
        if (this.filtersChangedWhilePending) {
          this.queryPending = true
          this.$nextTick(() => this.runQuery().catch(logQueryFailure))
        }
      }
    }
  },
  watch: {
    activeCompany: {
      immediate: true,
      async handler(activeCompany) {
        await Promise.all([
          this.fetchGroups({
            memberType: 'location',
            companyIds: this.selectActiveCompanyIds
          }),
          this.getUserLocationsByActiveCompany()
        ])

        const filters = {
          ...this.listFilters,
          locations: this.userActiveCompanyLocations.map((location) => location._id)
        }

        // Assigned before `applyFilters`, not after it. The assignment queues the `tagsFilters`
        // watcher, which reshapes `tags` and calls `applyFilters` again; `applyFilters` queues the
        // run. Queueing the watcher first makes it flush before that run, so the reshape is always
        // carried by the one dispatch. The other order leaves which lands first up to whether some
        // other component's subscription happened to queue a render watcher, and costs a second
        // list request whenever it loses.
        if (this.listFilters.tags) {
          this.tagsFilters = this.listFilters.tags
        }

        this.applyFilters(filters)
      }
    },
    limit: {
      handler(is) {
        this.setLimit(is || 50)
      },
      immediate: true
    },
    companyTags: {
      immediate: true,
      handler(companyTags) {
        this.tagOptions = companyTags
      }
    },
    tagsFilters(tags) {
      const tagActions = tags.map((t) => ({
        id: t._id || t.id,
        name: t.name
      }))

      this.applyFilters({
        ...this.listFilters,
        tags: tagActions
      })
    }
  }
}
</script>

<style scoped lang="scss">
.date-filter {
  top: 0px;
}
.filter-container {
  height: 80px;
  // overflow: hidden;
  float: left;
  // margin: 0px !important;
  padding: 0px !important;
}
.tag-filters {
  width: auto;
  min-width: 120px;
}
.customer-count {
  color: #6e84a3;
}
.customer-text {
  color: #95aac9;
}
@media (max-width: 991.98px) {
  .filter-container {
    // margin-right: 0rem !important;
    display: block !important;
    clear: both;
  }
}
</style>
